import {
    DeepReadonly,
    onMounted,
    onUnmounted,
    readonly,
    Ref,
    ref,
    UnwrapNestedRefs,
    watch,
} from '@vue/composition-api';

import { throttle } from 'lodash';

export const ScrollDirection = {
    UP: 'up',
    DOWN: 'down',
} as const;
export type ScrollDirection = typeof ScrollDirection[keyof typeof ScrollDirection];

interface ScrollDiffHandler {
    scrollDiff: DeepReadonly<UnwrapNestedRefs<Ref<number>>>;
    initialize(): string;
    destroy(key: string): void;
}

let scrollDiffHandler: ScrollDiffHandler | null = null;
let currentScrollY = window.scrollY;

type ScrollDirectionChangeParams = {
    diff: number;
    direction: ScrollDirection;
};

type ScrollDirectionOptions = {
    scrollRecognitionLimit?: number;
    onScrollDiffChange?: (params: Pick<ScrollDirectionChangeParams, 'diff'>) => void;
    onScrollDirectionChange?: (params: ScrollDirectionChangeParams) => void;
};

/**
 * To support use case customizability, the composable accepts callbacks and a scroll recognition limit to modify callback trigger sensibility.
 */
export const useScrollDirection = (options: ScrollDirectionOptions) => {
    if (!scrollDiffHandler) {
        scrollDiffHandler = createScrollDiffHandler();
    }

    const {
        scrollRecognitionLimit = 0,
        onScrollDiffChange,
        onScrollDirectionChange,
    } = options;

    const { scrollDiff, initialize, destroy } = scrollDiffHandler;
    const _scrollDirection = ref<ScrollDirection | null>(null);
    let _scrollDiffKey: string;

    onMounted(() => {
        _scrollDiffKey = initialize();
    });

    watch(scrollDiff, (diff: number) => {
        onScrollDiffChange?.({ diff });

        if (diff > scrollRecognitionLimit) {
            _scrollDirection.value = ScrollDirection.UP;
        } else if (diff < (0 - scrollRecognitionLimit)) {
            _scrollDirection.value = ScrollDirection.DOWN;
        } else {
            return;
        }

        currentScrollY = window.scrollY;

        onScrollDirectionChange?.({ diff, direction: _scrollDirection.value });
    });

    onUnmounted(() => {
        destroy(_scrollDiffKey);
    });

    return {
        scrollDirection: readonly(_scrollDirection),
    };
};

const scrollDiffKeys = new Set();
const scrollHandlerThrottleDurationMs = 120;

/**
 * This singleton serves as the only scroll handler for the entire application and it is already throttled.
 */
const createScrollDiffHandler = (): ScrollDiffHandler => {
    const _scrollDiff = ref(0);

    const _throttledScrollHandler = throttle(() => {
        _scrollDiff.value = window.scrollY - currentScrollY;
    }, scrollHandlerThrottleDurationMs);

    function initialize(): string {
        const key = Date.now().toString();

        if (!scrollDiffKeys.size) {
            window.addEventListener('scroll', _throttledScrollHandler, {
                passive: true,
            });

            currentScrollY = window.scrollY;
        }

        scrollDiffKeys.add(key);

        return key;
    }

    function destroy(key: string): void {
        if (!scrollDiffKeys.has(key)) {
            return;
        }

        scrollDiffKeys.delete(key);

        if (!scrollDiffKeys.size) {
            window.removeEventListener('scroll', _throttledScrollHandler);
            _throttledScrollHandler.cancel();
        }
    }

    return {
        scrollDiff: readonly(_scrollDiff),
        initialize,
        destroy,
    };
};
